<template>
  <transition name="drawer-fade">

    <div
      v-if="visible"
      class="overlay"
      role="dialog"
      aria-modal="true"
      @click.self="close"
    >

      <div class="panel">

        <!-- =====================================================
             HANDLE
        ====================================================== -->

        <div class="handle"></div>


        <!-- =====================================================
             HEADER
        ====================================================== -->

        <div class="panel-header">

          <div>
            <div class="kicker">
              Статус
            </div>

            <h3>
              Что у вас сейчас?
            </h3>

            <p>
              Выберите вариант, который лучше всего описывает вашу ситуацию.
            </p>
          </div>


          <button
            type="button"
            class="close-btn"
            aria-label="Закрыть"
            @click="close"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6 6L14 14"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />

              <path
                d="M14 6L6 14"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </button>

        </div>


        <!-- =====================================================
             OPTIONS
        ====================================================== -->

        <div class="options">

          <button
            v-for="opt in options"
            :key="opt.value"
            type="button"
            class="option-row"
            :class="{
              active: opt.value === localValue
            }"
            @click="select(opt.value)"
          >

            <span
              class="status-dot"
              :style="{
                backgroundColor: opt.color
              }"
            ></span>


            <div class="option-copy">

              <span class="option-title">
                {{ opt.label }}
              </span>

              <span class="option-description">
                {{ opt.description }}
              </span>

            </div>


            <span
              class="check"
              :class="{
                visible: opt.value === localValue
              }"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M4.5 9.2L7.4 12L13.5 6"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>

          </button>

        </div>


        <!-- =====================================================
             HINT
        ====================================================== -->

        <div class="hint">
          Статус помогает работодателям понимать,
          насколько вы открыты к новым предложениям.
        </div>

      </div>

    </div>

  </transition>
</template>


<script setup>

import {
  computed,
  ref,
  watch
} from "vue";


const props = defineProps({

  visible: {
    type: Boolean,
    default: false
  },

  modelValue: {
    type: Number,
    default: null
  },

  applyOnSelect: {
    type: Boolean,
    default: true
  }

});


const emit = defineEmits([
  "update:modelValue",
  "close",
  "apply"
]);


const localValue =
  ref(props.modelValue);


const options =
  computed(() => [

    {
      value: 1,
      label: "В активном поиске работы",
      description: "Готов рассматривать новые предложения",
      color: "#3D956F"
    },

    {
      value: 2,
      label: "В неспешном поиске работы",
      description: "Открыт к интересным возможностям",
      color: "#2277E6"
    },

    {
      value: 3,
      label: "Только нетворкинг",
      description: "Сейчас интересны знакомства и общение",
      color: "#E65122"
    },

    {
      value: 4,
      label: "Тут только для рекрутинга",
      description: "Использую Hr3 в основном для найма",
      color: "#E6AF22"
    }

  ]);


/* ============================================================
   SYNC
============================================================ */

watch(
  () => props.visible,

  visible => {

    if (visible) {
      localValue.value =
        props.modelValue;
    }

  }
);


watch(
  () => props.modelValue,

  value => {

    if (props.visible) {
      localValue.value =
        value;
    }

  }
);


/* ============================================================
   ACTIONS
============================================================ */

function close() {

  emit("close");

}


function select(value) {

  localValue.value =
    value;


  emit(
    "update:modelValue",
    value
  );


  if (
    props.applyOnSelect
  ) {

    emit(
      "apply",
      value
    );


    emit("close");

  }

}

</script>


<style scoped>

/* ============================================================
   OVERLAY
============================================================ */

.overlay {
  position: fixed;

  inset: 0;

  z-index: 3000;

  display: flex;
  align-items: flex-end;
  justify-content: center;

  padding: 12px;

  background:
    rgba(15, 18, 24, 0.38);

  backdrop-filter:
    blur(6px);

  -webkit-backdrop-filter:
    blur(6px);
}


/* ============================================================
   PANEL
============================================================ */

.panel {
  width:
    min(
      620px,
      100%
    );

  box-sizing: border-box;

  padding:
    9px
    18px
    18px;

  overflow: hidden;

  color: #272c33;

  background: #ffffff;

  border:
    1px solid
    rgba(
      20,
      35,
      60,
      0.08
    );

  border-radius: 24px;

  box-shadow:
    0
    28px
    80px
    rgba(
      15,
      23,
      42,
      0.22
    );
}


/* ============================================================
   HANDLE
============================================================ */

.handle {
  width: 36px;
  height: 4px;

  margin:
    0
    auto
    15px;

  background: #d9dde3;

  border-radius: 999px;
}


/* ============================================================
   HEADER
============================================================ */

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 16px;

  padding:
    0
    2px
    17px;
}


.kicker {
  margin-bottom: 5px;

  color: #8f959e;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  letter-spacing: 0.07em;

  text-transform: uppercase;
}


.panel-header h3 {
  margin: 0;

  color: #2d3239;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 16px;

  line-height: 1.3;
}


.panel-header p {
  max-width: 450px;

  margin:
    6px
    0
    0;

  color: #91969f;

  font-size: 9px;

  line-height: 1.45;
}


/* ============================================================
   CLOSE
============================================================ */

.close-btn {
  width: 34px;
  height: 34px;

  flex:
    0
    0
    34px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  color: #8e949d;

  background:
    rgba(
      30,
      40,
      55,
      0.045
    );

  border: none;
  border-radius: 10px;

  cursor: pointer;

  transition:
    color
    0.18s ease,
    background-color
    0.18s ease;
}


.close-btn:hover {
  color: #4d5560;

  background:
    rgba(
      30,
      40,
      55,
      0.075
    );
}


.close-btn svg {
  width: 14px;
  height: 14px;
}


/* ============================================================
   OPTIONS
============================================================ */

.options {
  display: flex;
  flex-direction: column;

  gap: 7px;
}


.option-row {
  width: 100%;

  display: grid;

  grid-template-columns:
    12px
    minmax(0, 1fr)
    30px;

  align-items: center;

  gap: 11px;

  padding:
    12px
    12px;

  box-sizing: border-box;

  text-align: left;

  color: inherit;

  background:
    #f8f9fb;

  border:
    1px solid
    transparent;

  border-radius: 14px;

  cursor: pointer;

  transition:
    background-color
    0.18s ease,
    border-color
    0.18s ease,
    transform
    0.18s ease;
}


.option-row:hover {
  background:
    #f4f7fb;

  transform:
    translateY(-1px);
}


.option-row.active {
  background:
    rgba(
      40,
      135,
      247,
      0.065
    );

  border-color:
    rgba(
      40,
      135,
      247,
      0.11
    );
}


/* ============================================================
   STATUS DOT
============================================================ */

.status-dot {
  width: 8px;
  height: 8px;

  flex-shrink: 0;

  border-radius: 50%;

  box-shadow:
    0
    0
    0
    4px
    rgba(
      80,
      100,
      130,
      0.05
    );
}


/* ============================================================
   COPY
============================================================ */

.option-copy {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 3px;
}


.option-title {
  color: #3c424a;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  line-height: 1.35;
}


.option-description {
  color: #9a9fa7;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;

  line-height: 1.4;
}


/* ============================================================
   CHECK
============================================================ */

.check {
  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  background: #347fe7;

  border-radius: 9px;

  opacity: 0;

  transform:
    scale(0.8);

  transition:
    opacity
    0.18s ease,
    transform
    0.18s ease;
}


.check.visible {
  opacity: 1;

  transform:
    scale(1);
}


.check svg {
  width: 14px;
  height: 14px;
}


/* ============================================================
   HINT
============================================================ */

.hint {
  margin-top: 13px;

  padding:
    11px
    12px;

  color: #90959e;

  background:
    rgba(
      30,
      40,
      55,
      0.03
    );

  border-radius: 11px;

  font-size: 8px;

  line-height: 1.45;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.panel {
  color: #f0f1f3;

  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0
    35px
    90px
    rgba(
      0,
      0,
      0,
      0.48
    );
}


html[data-theme="dark"]
.handle {
  background: #484b52;
}


html[data-theme="dark"]
.panel-header h3 {
  color: #f0f1f3;
}


html[data-theme="dark"]
.panel-header p {
  color: #858a93;
}


html[data-theme="dark"]
.close-btn {
  color: #9297a0;

  background:
    rgba(
      255,
      255,
      255,
      0.045
    );
}


html[data-theme="dark"]
.close-btn:hover {
  color: #ffffff;

  background:
    rgba(
      255,
      255,
      255,
      0.075
    );
}


html[data-theme="dark"]
.option-row {
  background:
    rgba(
      255,
      255,
      255,
      0.03
    );
}


html[data-theme="dark"]
.option-row:hover {
  background:
    rgba(
      255,
      255,
      255,
      0.045
    );
}


html[data-theme="dark"]
.option-row.active {
  background:
    rgba(
      40,
      135,
      247,
      0.10
    );

  border-color:
    rgba(
      80,
      150,
      245,
      0.13
    );
}


html[data-theme="dark"]
.option-title {
  color: #e1e3e7;
}


html[data-theme="dark"]
.option-description {
  color: #818690;
}


html[data-theme="dark"]
.hint {
  color: #858a93;

  background:
    rgba(
      255,
      255,
      255,
      0.028
    );
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 560px) {

  .overlay {
    padding:
      8px;
  }


  .panel {
    padding:
      8px
      13px
      14px;

    border-radius: 21px;
  }


  .panel-header {
    padding-bottom: 14px;
  }


  .panel-header h3 {
    font-size: 15px;
  }


  .option-row {
    padding:
      11px
      10px;
  }

}


/* ============================================================
   TRANSITION
============================================================ */

.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition:
    opacity
    0.2s ease;
}


.drawer-fade-enter-active
.panel,
.drawer-fade-leave-active
.panel {
  transition:
    transform
    0.22s
    cubic-bezier(
      0.22,
      1,
      0.36,
      1
    ),
    opacity
    0.2s ease;
}


.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
}


.drawer-fade-enter-from
.panel,
.drawer-fade-leave-to
.panel {
  opacity: 0;

  transform:
    translateY(18px);
}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .option-row,
  .close-btn,
  .check,
  .drawer-fade-enter-active,
  .drawer-fade-leave-active,
  .drawer-fade-enter-active .panel,
  .drawer-fade-leave-active .panel {
    transition: none;
  }

}

</style>